import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';

const OrderConfirmation: React.FC = () => {
  usePageTitle('Order Confirmation');
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as any;
  const hasOrderData = state && state.orderData;

  // If no order state, redirect back to checkout
  React.useEffect(() => {
    if (!hasOrderData) {
      navigate('/checkout');
    }
  }, [hasOrderData, navigate]);

  if (!hasOrderData) {
    return null;
  }

  return (
    <div className="order-confirmation-page">
      <section className="confirmation-header">
        <div className="section-container">
          <div className="confirmation-success-icon">✓</div>
          <h1 className="page-title">Thank You for Your Order!</h1>
          <p className="confirmation-subtitle">
            Your enrollment request has been received and is being processed.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="section-container">
          <div className="confirmation-content">
            <div className="confirmation-card">
              <h2>What Happens Next</h2>
              <ol className="confirmation-steps">
                <li className="confirmation-step">
                  <span className="step-number">1</span>
                  <div className="step-content">
                    <h3>Verification</h3>
                    <p>We'll verify your enrollment request within 1-2 business days.</p>
                  </div>
                </li>
                <li className="confirmation-step">
                  <span className="step-number">2</span>
                  <div className="step-content">
                    <h3>Confirmation Email</h3>
                    <p>You'll receive a confirmation email with your course details and login instructions.</p>
                  </div>
                </li>
                <li className="confirmation-step">
                  <span className="step-number">3</span>
                  <div className="step-content">
                    <h3>Course Access</h3>
                    <p>Return to the site, sign in, and access your course materials and pre-work.</p>
                  </div>
                </li>
                <li className="confirmation-step">
                  <span className="step-number">4</span>
                  <div className="step-content">
                    <h3>Get Started</h3>
                    <p>Complete any pre-work requirements and prepare for your upcoming session.</p>
                  </div>
                </li>
              </ol>
            </div>

            <div className="confirmation-card">
              <h2>Order Summary</h2>
              <div className="summary-details">
                <div className="summary-row">
                  <span className="summary-label">Plan:</span>
                  <span className="summary-value">{state.orderData.tier}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Students:</span>
                  <span className="summary-value">{state.orderData.students.length}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Payment Method:</span>
                  <span className="summary-value">
                    {state.paymentMethod === 'purchase_order' ? 'Purchase Order' : 'Prepaid or Voucher'}
                  </span>
                </div>
                <div className="summary-row summary-total">
                  <span className="summary-label">Total:</span>
                  <span className="summary-value">${state.total.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="confirmation-card">
              <h2>Questions?</h2>
              <p>If you have any questions about your enrollment, please don't hesitate to contact us:</p>
              <div className="contact-info">
                <p>
                  <strong>Email:</strong>{' '}
                  <a href="mailto:training@patriotconsulting.com">training@patriotconsulting.com</a>
                </p>
                <p>
                  <strong>Phone:</strong> <a href="tel:1-844-560-4630">1-844-560-4630</a>
                </p>
              </div>
            </div>

            <div className="confirmation-actions">
              <Link to="/" className="btn btn-primary">
                Return to Home
              </Link>
              <Link to="/courses" className="btn btn-outline">
                Browse More Courses
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OrderConfirmation;
